import type { OEPFileKind } from './entities.js';

/**
 * The one definition of what a presenter may upload as an OEP draft/final: enforced by
 * OEPFileResolver (apps/MJAPI/src/oep/files.ts) and mirrored by the Presenter view's file drop.
 * Messages are the user-facing refusal copy (task 25 brief, "Writes: Upload").
 */
export const OEP_UPLOAD_RULES = {
    Types: ['application/pdf', 'application/vnd.openxmlformats-officedocument.presentationml.presentation', 'image/png', 'image/jpeg', 'video/mp4', 'video/quicktime', 'video/x-msvideo'],
    MaxBytes: 100 * 1024 * 1024,
    Messages: { Type: 'Upload a PDF, PowerPoint, poster image or video.', Size: 'Files can be up to 100 MB.' },
} as const satisfies { Types: readonly string[]; MaxBytes: number; Messages: { Type: string; Size: string } };

/** Every OEP presentation object lives under this prefix: `oep/<PresentationID>/<kind>/<time>/<name>` (MJAPI BlobPathFor). */
export const OEP_BLOB_PREFIX = 'oep/';
/** metadata/file-categories/oep-file-categories.json — tracked MJ: File Categories rows, same IDs on every environment. */
export const OEP_FILE_CATEGORY_ID = {
    Draft: '0E900000-0000-4000-9000-000000000042',
    Final: '0E900000-0000-4000-9000-000000000043',
} as const satisfies Readonly<Record<OEPFileKind, string>>;

/**
 * The key as a blob store resolves it: `\` → `/`, leading slashes dropped, `.`/`..`/empty segments resolved
 * POSIX-style (an HTTP client normalizes `a/../oep/x` in the request URL), lower-cased for the comparison.
 * A `..` that climbs above the root yields null — never a legitimate key.
 */
export function NormalizeOEPStorageKey(name: string): string | null {
    const out: string[] = [];
    for (const seg of name.replace(/\\/g, '/').split('/')) {
        if (seg === '' || seg === '.') continue;
        if (seg === '..') { if (out.length === 0) return null; out.pop(); continue; }
        out.push(seg);
    }
    return out.join('/').toLowerCase();
}

/** True when `name` (an object key, path or directory) is, or is inside, the OEP namespace — or cannot be normalized. */
export function IsOEPStorageKey(name: string | null | undefined): boolean {
    if (typeof name !== 'string') return false;
    const n = NormalizeOEPStorageKey(name);
    const root = OEP_BLOB_PREFIX.replace(/\/$/, '');
    return n === null || n === root || n.startsWith(OEP_BLOB_PREFIX);
}
